import { NetworkStatus } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EllipsisIcon,
  InboxIcon,
  LockIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import type { PanelImperativeHandle } from "react-resizable-panels";

import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import {
  SelectableCardList,
  SelectableCardListSearchField,
} from "@/components/molecules/SelectableCardList";
import { SelectableListSelectionBar } from "@/components/molecules/SelectableListSelectionBar";
import {
  SelectableListItemRow,
  SelectableListItemRowOverline,
  SelectableListItemRowTitleEnd,
  getPendingChangesStats,
  SELECTABLE_LIST_ITEM_ROW_THREAD_HEIGHT,
  SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT,
  type SelectableListItemRowProps,
} from "@/components/molecules/SelectableListItemRow";
import { List } from "@/components/organisms/List";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useDevice } from "@/hooks/useDevice";
import {
  AppLayoutContentPanel,
  AppLayoutPanel,
  AppLayoutPanelGroup,
  AppLayoutPanelResizeHandle,
} from "@/layouts/AppLayout";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/components/ui/utils";

import { THREAD_LIST_FILTERS, THREAD_LIST_PAGE_SIZE } from "./Thread.content";
import {
  MESSAGES_PER_PAGE,
  removeThreadsFromConnections,
} from "./Thread.utils";

type ThreadListItem = {
  id: string;
  identifier: string;
  name: string;
  summary: string | null;
  lastUserMessagePreview: string | null;
  createdAt: string;
  updatedAt: string;
  isRead: boolean;
  isArchived: boolean;
  source: string;
  entity: {
    id: string;
    identifier: string;
    name: string;
    isUserEntity?: boolean | null;
  };
  pendingSession: {
    identifier: string;
    status: string;
    changeCount: number;
    pendingChanges?: Array<{
      changeType: string;
      status: string;
      deltas?: Array<{ fieldName: string }> | null;
    }> | null;
  } | null;
};

type ThreadPanelFilter = keyof typeof THREAD_LIST_FILTERS;

const THREAD_LIST_SKELETON_COUNT = 8;

/** What the list's rows are while it loads: a typical thread, drawn as any is. */
const THREAD_LIST_PLACEHOLDER: ThreadListItem = {
  id: "placeholder",
  identifier: "placeholder",
  name: "Reconcile last month's card payments",
  summary: "Matched the card payments to their receipts and booked the rest.",
  lastUserMessagePreview: "Can you match these to their receipts?",
  createdAt: "2026-01-15T10:00:00Z",
  updatedAt: "2026-01-15T10:00:00Z",
  isRead: true,
  isArchived: false,
  source: "user",
  entity: {
    id: "placeholder-entity",
    identifier: "placeholder-entity",
    name: "Northwind Traders",
  },
  pendingSession: null,
};

function FilterToggle({
  activeFilter,
  onFilterChange,
}: {
  activeFilter: ThreadPanelFilter;
  onFilterChange: (f: ThreadPanelFilter) => void;
}) {
  return (
    <ToggleGroup
      type="single"
      size="sm"
      variant="outline"
      value={activeFilter}
      // Pressing the active filter again would clear the group; a list always
      // has one filter, so that press changes nothing.
      onValueChange={(value) =>
        value && onFilterChange(value as ThreadPanelFilter)
      }
    >
      <ToggleGroupItem value="all">All</ToggleGroupItem>
      <ToggleGroupItem value="pending">Pending</ToggleGroupItem>
    </ToggleGroup>
  );
}

function ThreadRowMenu({ onDelete }: { onDelete: () => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const actions = [{ id: "delete", label: "Delete", icon: Trash2Icon }];

  return (
    <Popper
      anchor={
        <Button variant="ghost" size="icon-xs" aria-label="Thread actions">
          <Icon ReactIcon={EllipsisIcon} size="sm" />
        </Button>
      }
      isOpenState={[isOpen, setIsOpen]}
      placement="bottom-end"
      isClickable
      isHoverable={false}
    >
      <List
        data={actions}
        onItemClick={() => {
          setIsOpen(false);
          onDelete();
        }}
        size="sm"
        className="min-w-40"
      >
        <List.Items>
          {(action: (typeof actions)[number]) => (
            <List.Item value={action}>
              <div className="flex items-center gap-2 text-destructive">
                <Icon ReactIcon={action.icon} size="sm" />
                <div className="font-medium text-xs leading-snug text-foreground">
                  {action.label}
                </div>
              </div>
            </List.Item>
          )}
        </List.Items>
      </List>
    </Popper>
  );
}

function ThreadRow({
  thread,
  compact,
  isLoading,
  checkbox,
  trailing,
}: {
  thread: ThreadListItem;
  /** Drops the summary line and steps the text down a size. */
  compact: boolean;
  /** The list's placeholder while it loads. */
  isLoading: boolean;
} & Pick<SelectableListItemRowProps, "checkbox" | "trailing">) {
  const size = compact ? "sm" : "md";

  const hasPending =
    thread.pendingSession?.status === "pending" &&
    (thread.pendingSession?.changeCount ?? 0) > 0;

  const changeStats = hasPending
    ? getPendingChangesStats(thread.pendingSession?.pendingChanges)
    : undefined;

  const messagePreview = thread.lastUserMessagePreview?.trim() || undefined;
  const summaryText = thread.summary?.trim() || undefined;

  const entityName = thread.entity?.name?.trim() || undefined;
  // A person's own chat is private: said so, with a lock - not their name.
  const isPrivate = thread.entity?.isUserEntity === true;

  return (
    <SelectableListItemRow
      size={size}
      overline={
        isPrivate ? (
          <SelectableListItemRowOverline>
            <span
              data-testid="thread-row-private"
              className="inline-flex items-center gap-1"
            >
              <Icon ReactIcon={LockIcon} size="xs" />
              Private
            </span>
          </SelectableListItemRowOverline>
        ) : entityName ? (
          <SelectableListItemRowOverline nodeId={thread.entity?.id}>
            {entityName}
          </SelectableListItemRowOverline>
        ) : undefined
      }
      overlineEnd={
        <SelectableListItemRowTitleEnd size={size}>
          {Utils.fTimeAgo(thread.updatedAt ?? thread.createdAt)}
        </SelectableListItemRowTitleEnd>
      }
      title={thread.name || "Untitled"}
      description={messagePreview}
      summary={compact ? undefined : summaryText}
      summaryMaxLines={2}
      emphasized={!thread.isRead}
      checkbox={checkbox}
      trailing={trailing}
      changeStats={changeStats}
      isLoading={isLoading}
    />
  );
}

// Isolated so its local state (search, filter) never causes Thread to rerender
function ThreadListPanel({
  compact,
  onClose,
  onPick,
}: {
  compact: boolean;
  /** Shuts it from its own head, where it drops down over the chat. */
  onClose?: () => void;
  /** A thread picked from it - on a phone, where it covers the chat, it goes. */
  onPick?: () => void;
}) {
  const apolloClient = useApolloClient();
  const activeThreadNodeId = State((s) => s.activeThreadNodeId);
  // The whole width of a phone - or of a window as narrow - has room for each
  // row's summary at its size; only the chat panel's narrow strip steps them
  // down. A phone still takes the per-row menu over checkboxes: a tap, not a
  // pointer's selection.
  const { isNarrow } = useDevice();
  const compactRows = compact && !isNarrow;
  const [checkedIds, setCheckedIds] = useState<Record<string, boolean>>({});
  const [activeFilter, setActiveFilter] = useState<ThreadPanelFilter>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data, loading, networkStatus } = useBackendQuery(
    Queries.THREADS_CONNECTION_QUERY,
    {
      variables: {
        filter: THREAD_LIST_FILTERS[activeFilter],
        first: THREAD_LIST_PAGE_SIZE,
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );

  const threadsLoading =
    (loading ||
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.refetch) &&
    !(
      (data as { threadsConnection?: { data?: { edges?: unknown[] } } })
        ?.threadsConnection?.data?.edges?.length ?? 0
    );

  const { mutate: deleteThreads } = useBackendMutation({
    mutation: Mutations.DELETE_NODES,
    callbacks: {
      onError: () => {
        void apolloClient.refetchQueries({ include: ["Threads"] });
      },
    },
  });

  const deleteThreadIds = useCallback(
    (ids: string[]) => {
      removeThreadsFromConnections(apolloClient.cache, ids);
      deleteThreads({
        variables: { input: { ids } },
        update: (cache) => {
          removeThreadsFromConnections(cache, ids);
        },
      });
    },
    [apolloClient.cache, deleteThreads],
  );

  const selectionButtons = useMemo(
    () => [
      {
        variant: "destructive" as const,
        children: "Delete",
        onClick: deleteThreadIds,
      },
    ],
    [deleteThreadIds],
  );

  const allThreads: ThreadListItem[] =
    (data as any)?.threadsConnection?.data?.edges?.map((e: any) => e.node) ??
    [];
  const threads = searchTerm.trim()
    ? allThreads.filter((t) => {
        const term = searchTerm.toLowerCase();
        return (
          (t.name ?? "").toLowerCase().includes(term) ||
          (t.summary ?? "").toLowerCase().includes(term)
        );
      })
    : allThreads;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Header: filter tabs */}
      <div className="h-12 shrink-0 flex items-center [border-bottom:1px_solid_var(--border)] bg-background pl-2 pr-2 gap-1">
        <FilterToggle
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
        {onClose ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Close thread list"
            onClick={onClose}
            className="ml-auto"
          >
            <Icon ReactIcon={XIcon} size="sm" />
          </Button>
        ) : null}
      </div>

      {/* Search bar */}
      <SelectableCardListSearchField
        align="inset-rows"
        placeholder="Search"
        value={searchTerm}
        onChange={setSearchTerm}
        inputRef={searchInputRef}
        containerClassName="h-12 [border-bottom:1px_solid_var(--border)] bg-background"
      />

      {/* Virtualized thread list (owns the scroll container) */}
      <div className="flex-1 min-h-0 flex flex-col">
        <SelectableCardList
          size={compactRows ? "sm" : "md"}
          variant="soft"
          layout="cards"
          items={threads}
          loading={threadsLoading}
          loadingItemCount={THREAD_LIST_SKELETON_COUNT}
          placeholderItem={THREAD_LIST_PLACEHOLDER}
          estimatedItemHeight={
            compactRows
              ? SELECTABLE_LIST_ITEM_ROW_WITH_OVERLINE_HEIGHT
              : SELECTABLE_LIST_ITEM_ROW_THREAD_HEIGHT
          }
          getItemKey={(thread) => thread.id}
          isItemActive={(thread) => activeThreadNodeId === thread.id}
          scrollToActiveKey={activeThreadNodeId}
          keyboardSourceRef={searchInputRef}
          onItemClick={(thread, _index, meta) => {
            const { openThreadTab, pinThreadTab } = State.getState();
            openThreadTab(thread.id, { label: thread.name, preview: true });
            onPick?.();
            // The second click of a double-click keeps the tab.
            if (meta?.clickCount === 2) {
              pinThreadTab(thread.id);
            }
          }}
          // Warm each thread before it is opened: the panel reads it
          // cache-first, so the click then renders without its spinner.
          onItemHover={(thread) => {
            void apolloClient
              .query({
                query: Queries.THREAD_WITH_MESSAGES_QUERY,
                variables: { id: thread.id, last: MESSAGES_PER_PAGE },
                fetchPolicy: "cache-first",
              })
              // A failed warm-up must not surface; opening the thread retries.
              .catch(() => undefined);
          }}
          emptyState={{
            ReactIcon: InboxIcon,
            title: searchTerm.trim() ? "No results" : "No conversations",
            description: searchTerm.trim()
              ? "Try a different search term."
              : "Start a thread or check back later.",
          }}
          footer={
            compact ? undefined : (
              <SelectableListSelectionBar
                nameAsSingular="thread"
                itemCount={threads.length}
                checkedRows={checkedIds}
                setCheckedRows={setCheckedIds}
                buttons={selectionButtons}
              />
            )
          }
          renderItem={({ item: thread, isLoading }) => (
            <ThreadRow
              thread={thread}
              compact={compactRows}
              isLoading={isLoading}
              checkbox={
                compact
                  ? undefined
                  : {
                      checked: checkedIds[thread.id] === true,
                      onToggle: () => {
                        setCheckedIds({
                          ...checkedIds,
                          [thread.id]: !checkedIds[thread.id],
                        });
                      },
                    }
              }
              trailing={
                compact ? (
                  <ThreadRowMenu
                    onDelete={() => deleteThreadIds([thread.id])}
                  />
                ) : undefined
              }
            />
          )}
        />
      </div>
    </div>
  );
}

const MIN_LIST_WIDTH = 200;
const DEFAULT_LIST_WIDTH = 300;

/**
 * Lays out ``children`` (the thread) with the thread list, under the thread
 * tab bar. In the thread view the list is docked in a collapsible panel on its
 * right, and opens, closes and resizes like the app's other panels; open from
 * the start, it takes its width in pixels, and keeps it as the view widens or
 * narrows. ``id`` keeps each host's panel ids unique.
 *
 * In the chat panel (``compact``) - too narrow to give a strip of it to the
 * list - the list drops down from under the tab bar instead, over the top of
 * the chat, and goes back up as it closes.
 *
 * On a phone - or in a window as narrow - there is no room for a strip of
 * either: the list drops down over all of the chat, and goes back up as a
 * thread is picked or a new one started, so the two are one at a time.
 */
export function ThreadListLayout({
  id,
  isOpen,
  onOpenChange,
  compact = false,
  listWidth,
  onListResize,
  children,
}: {
  id: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /**
   * Narrow layout for the global thread panel: the list drops down from the
   * top rather than opening on the right, and its rows drop the summary, use
   * smaller text, and swap bulk-select checkboxes for a per-row "…" menu.
   */
  compact?: boolean;
  /** How wide the list opens, in pixels, rather than its own default. */
  listWidth?: number | null;
  /** The open list's width in pixels, each time it changes. */
  onListResize?: (width: number) => void;
  children: React.ReactNode;
}) {
  const panelRef = useRef<PanelImperativeHandle>(null);
  const threadPanelId = `${id}-thread`;
  const listPanelId = `${id}-list`;
  // The list queries threads, so mount it on first open rather than with the
  // global thread panel on every page; after that it stays mounted so closing
  // animates over the list instead of an empty panel.
  const [hasOpened, setHasOpened] = useState(isOpen);
  if (isOpen && !hasOpened) {
    setHasOpened(true);
  }
  const { isNarrow } = useDevice();
  // A thread opened another way - a new one, a tab - is the chat to show.
  const activeThreadNodeId = State((s) => s.activeThreadNodeId);
  const shownThreadRef = useRef(activeThreadNodeId);
  useEffect(() => {
    if (shownThreadRef.current === activeThreadNodeId) return;
    shownThreadRef.current = activeThreadNodeId;
    if (isNarrow && isOpen) onOpenChange(false);
  }, [activeThreadNodeId, isNarrow, isOpen, onOpenChange]);

  if (compact || isNarrow) {
    return (
      <div
        // Up, it is out of sight above the chat rather than over the tabs.
        className="relative flex-1 min-h-0 flex flex-col overflow-hidden"
      >
        {children}
        <div
          data-testid="thread-list-drop-down"
          {...(!isOpen ? { inert: true } : {})}
          // Over all of the chat on a phone, it has no foot to edge.
          // Hidden once it has gone back up, not as it starts to.
          className={cn(
            "absolute top-0 left-0 right-0",
            isNarrow ? "h-full" : "h-[min(60%,520px)]",
            "z-2 flex flex-col bg-background",
            isNarrow
              ? ""
              : "[border-bottom-width:1px] [border-bottom-style:solid] border-border shadow-sm",
            isOpen
              ? "transform-[translateY(0)]"
              : "transform-[translateY(-100%)]",
            isOpen ? "visible" : "invisible",
            isOpen
              ? "transition-normal [transition-duration:200ms] [transition-timing-function:ease-out] delay-[0s] [transition-property:transform]"
              : "[transition-behavior:normal,normal] [transition-duration:200ms,0s] [transition-timing-function:ease-in,linear] delay-[0s,200ms] [transition-property:transform,visibility]",
            "motion-reduce:transition-normal [@media(prefers-reduced-motion:_reduce)]:[transition-duration:0s] [@media(prefers-reduced-motion:_reduce)]:[transition-timing-function:ease] motion-reduce:delay-[0s] motion-reduce:transition-none",
          )}
        >
          {hasOpened && (
            <ThreadListPanel
              compact
              onClose={() => onOpenChange(false)}
              onPick={isNarrow ? () => onOpenChange(false) : undefined}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <AppLayoutPanelGroup
      direction="horizontal"
      id={`${id}-list-group`}
      defaultLayout={
        isOpen ? undefined : { [threadPanelId]: 100, [listPanelId]: 0 }
      }
    >
      <AppLayoutContentPanel
        id={threadPanelId}
        minSize="30%"
        style={{ backgroundColor: "transparent" }}
      >
        {children}
      </AppLayoutContentPanel>
      <AppLayoutPanelResizeHandle disabled={!isOpen} />
      <AppLayoutPanel
        id={listPanelId}
        panelRef={panelRef}
        defaultSize={listWidth ?? DEFAULT_LIST_WIDTH}
        minSize={MIN_LIST_WIDTH}
        collapsible={true}
        collapsedSize={0}
        disabled={!isOpen}
        isMinimized={!isOpen}
        groupResizeBehavior="preserve-pixel-size"
        // Not while it shuts: that is the way out, not a width it was left at.
        onResize={({ inPixels }) => {
          if (isOpen && inPixels > 0) {
            onListResize?.(inPixels);
          }
        }}
        onCollapse={() => onOpenChange(false)}
        onExpand={() => onOpenChange(true)}
      >
        <div
          {...(!isOpen ? { inert: true } : {})}
          className="flex-1 min-h-0 flex flex-col"
        >
          {hasOpened && <ThreadListPanel compact={false} />}
        </div>
      </AppLayoutPanel>
    </AppLayoutPanelGroup>
  );
}
